import useHandlePostScanQR from 'entity/event/useHandlePostScanQR'
import usePostScanUserQR from 'entity/event/usePostScanUserQR'
import useGetMyStore from 'entity/store/useGetMyStore'
import {useEffect, useRef} from 'react'
import {
	ROLE_FRANCHISE_MANAGER,
	ROLE_STORE_MANAGER,
} from 'shared/constant/lookup/role'
import useQrScanner from 'shared/hooks/useQrScanner'
import useRoleCookie from 'shared/model/useRoleCookie'
import QrScanStyles from 'shared/style/qrScanStyles'
import QrScannerControls from 'shared/ui/QrScannerControls'
import QrScannerError from 'shared/ui/QrScannerError'
import QrScannerInstructions from 'shared/ui/QrScannerInstructions'
import QrScannerVideo from 'shared/ui/QrScannerVideo'
import StoreEventBottomSheet from 'shared/ui/ScanResultBottomSheet/StoreEventBottomSheet'

const QrScanStore = () => {
	const [roleCookie] = useRoleCookie()
	const [myStoreData] = useGetMyStore(
		roleCookie === ROLE_STORE_MANAGER || roleCookie === ROLE_FRANCHISE_MANAGER
	)

	const {
		isInitializing,
		scannedData,
		error,
		hasScannedData,
		videoRef,
		clearAndRescan,
	} = useQrScanner()

	const [
		postScanQR,
		scanResult,
		isLoading,
		clearScanResult,
		isBottomSheetOpen,
		handleRefetch,
	] = usePostScanUserQR(clearAndRescan)

	// Track if we've already made API call for current scan
	const hasCalledApiRef = useRef(false)

	useHandlePostScanQR(
		hasScannedData,
		scannedData,
		scanResult,
		isBottomSheetOpen,
		hasCalledApiRef,
		postScanQR
	)

	// Reset API call flag when starting a new scan
	useEffect(() => {
		if (!hasScannedData) {
			hasCalledApiRef.current = false
		}
	}, [hasScannedData])

	return (
		<>
			<QrScanStyles.Container>
				{myStoreData && (
					<QrScanStyles.Title>
						{myStoreData?.data?.store_name}
					</QrScanStyles.Title>
				)}

				<QrScanStyles.SubTitle>Scan the user QR code</QrScanStyles.SubTitle>

				<QrScanStyles.Wrapper>
					<QrScannerVideo
						videoRef={videoRef}
						isLoading={isLoading}
						isInitializing={isInitializing}
					/>
					<QrScannerControls
						hasScannedData={hasScannedData}
						onRescan={clearAndRescan}
						isLoading={isLoading}
					/>
				</QrScanStyles.Wrapper>

				<QrScannerError error={error} />

				<QrScannerInstructions instructions={instructions} showTitle={false} />
			</QrScanStyles.Container>

			{isBottomSheetOpen && scanResult && (
				<StoreEventBottomSheet
					// isOpen={true}
					isOpen={isBottomSheetOpen}
					onClose={() => {
						clearScanResult() // Clear API result first
						clearAndRescan() // Then restart scanning
					}}
					// scanData={exampleData}
					scanData={scanResult}
					handleRefetch={handleRefetch}
					isLoading={isLoading}
				/>
			)}
		</>
	)
}

export default QrScanStore

const instructions = [
	'💡사용자의 QR 코드를 스캔하여 참가할 수 있는 대회를 확인하세요.',
]

export const exampleData = {
	user_info: {
		idx: 1315,
		nickname: 'user2z',
		name: 'user2',
		contact: '01051541552',
		profile_img:
			'https://orcalabs.s3.ap-northeast-2.amazonaws.com/profile_image/1755585837503.jpg',
		pro_grade_flag: 1,
	},
	participation_tickets_count: 0,
	store_tickets_count: '020205150501571150896',
	store_tickets_breakdown: {
		typed_tickets: '020205150501571150896',
	},
	eligible_competitions: [
		{
			type: 'store_ticket',
			event_idx: 86,
			event_name: "Howl's moving caslte... [STORE EVENT]",
			event_date: '2025-09-29T05:23:00.000Z',
			event_state: 2,
			required_tickets: 5,
			pro_grade_required: 1,
			store_info: {
				manager_idx: 6,
				store_name: 'B점111',
				franchise_name: '강동수 토너리그1',
			},
			ticket_requirements: {
				specific_type_required: true,
				required_ticket_type_idx: 3,
				required_ticket_type_name: '기본 이용권',
				required_ticket_description: null,
			},
			available_tickets: {
				total_available: 156,
				can_fulfill_requirement: true,
				typed_tickets_detail: [
					{
						ticket_type_name: '기본 이용권',
						ticket_type_idx: 3,
						available_count: '50',
						expires_at: '2025-10-15T01:59:18.675Z',
						expires_status: '21일 남음',
						is_required_type: true,
					},
					{
						ticket_type_name: '기본 이용권',
						ticket_type_idx: 3,
						available_count: '50',
						expires_at: '2025-10-15T02:02:12.516Z',
						expires_status: '21일 남음',
						is_required_type: true,
					},
					{
						ticket_type_name: '기본 이용권',
						ticket_type_idx: 3,
						available_count: '1',
						expires_at: '2025-10-15T02:04:14.080Z',
						expires_status: '21일 남음',
						is_required_type: true,
					},
					{
						ticket_type_name: '기본 이용권',
						ticket_type_idx: 3,
						available_count: '5',
						expires_at: '2025-10-19T14:34:19.769Z',
						expires_status: '26일 남음',
						is_required_type: true,
					},
					{
						ticket_type_name: null,
						ticket_type_idx: 3,
						available_count: '50',
						expires_at: null,
						expires_status: '영구',
						is_required_type: true,
					},
				],
			},
			event_timer: {
				bb: null,
				sb: null,
				ante: null,
				level: null,
				add_on: 1,
				next_bb: null,
				next_sb: null,
				duration:
					'[[1,"5","10",20,20],[2,"10","15",30,30],[3,"15","20",40,40],["break","5",null,null,null],[4,"20","25",50,50],[5,"25","30",60,60],[6,"30","35",70,70],["break","5",null,null,null],[7,"35","40",80,80],[8,"40","45",90,90],[9,"45","50",100,100]]',
				re_entry: 2,
				next_ante: null,
				early_chip: 1000,
				is_reg_end: false,
				next_lavel: null,
				pause_date: null,
				start_time: 1758266087860,
				add_on_chip: 5000,
				early_count: 0,
				event_state: 'taking',
				is_level_up: false,
				rest_player: 0,
				total_stack: 8040,
				current_time: 1758596931505,
				elapsed_time: 0,
				is_early_end: false,
				total_minute: 0,
				total_player: 4,
				re_entry_chip: 1000,
				reg_end_level: 3,
				starting_chip: 10,
				duration_index: -1,
				reg_end_minute: 0,
				early_end_level: 5,
				next_break_minute: 0,
			},
			participation_history: {
				count: 3,
				max_reentry: 5,
				remaining_reentries: 2,
			},
			user_can_join: false,
			has_joined: true,
			can_eliminate: false,
			can_addon: false,
			can_reenter: false,
		},
	],
	store_tickets_detail: {
		typed_tickets: [
			{
				ticket_type_idx: 4,
				expires_at: '2025-10-09T03:59:37.820Z',
				ticket_type_name: 'pzcuong',
				expiration_days: 60,
				ticket_source: 'typed',
				available_count: '20',
				expires_status: '15일 남음',
				batch_created_at: '2025-09-09T12:59:38.106Z',
			},
			{
				ticket_type_idx: 4,
				expires_at: '2025-10-09T04:03:09.193Z',
				ticket_type_name: 'pzcuong',
				expiration_days: 60,
				ticket_source: 'typed',
				available_count: '20',
				expires_status: '15일 남음',
				batch_created_at: '2025-09-09T13:03:09.479Z',
			},
			{
				ticket_type_idx: 4,
				expires_at: '2025-10-14T11:35:33.605Z',
				ticket_type_name: 'pzcuong',
				expiration_days: 60,
				ticket_source: 'typed',
				available_count: '5',
				expires_status: '21일 남음',
				batch_created_at: '2025-09-14T20:35:33.592Z',
			},
			{
				ticket_type_idx: 4,
				expires_at: '2025-10-14T11:39:32.350Z',
				ticket_type_name: 'pzcuong',
				expiration_days: 60,
				ticket_source: 'typed',
				available_count: '1',
				expires_status: '21일 남음',
				batch_created_at: '2025-09-14T20:39:32.348Z',
			},
			{
				ticket_type_idx: 3,
				expires_at: '2025-10-15T01:59:18.675Z',
				ticket_type_name: '기본 이용권',
				expiration_days: 30,
				ticket_source: 'typed',
				available_count: '50',
				expires_status: '21일 남음',
				batch_created_at: '2025-09-15T10:59:18.666Z',
			},
			{
				ticket_type_idx: 3,
				expires_at: '2025-10-15T02:02:12.516Z',
				ticket_type_name: '기본 이용권',
				expiration_days: 30,
				ticket_source: 'typed',
				available_count: '50',
				expires_status: '21일 남음',
				batch_created_at: '2025-09-15T11:02:12.512Z',
			},
			{
				ticket_type_idx: 3,
				expires_at: '2025-10-15T02:04:14.080Z',
				ticket_type_name: '기본 이용권',
				expiration_days: 30,
				ticket_source: 'typed',
				available_count: '1',
				expires_status: '21일 남음',
				batch_created_at: '2025-09-15T11:04:14.078Z',
			},
			{
				ticket_type_idx: 3,
				expires_at: '2025-10-19T14:34:19.769Z',
				ticket_type_name: '기본 이용권',
				expiration_days: 30,
				ticket_source: 'typed',
				available_count: '5',
				expires_status: '26일 남음',
				batch_created_at: '2025-09-19T23:34:19.727Z',
			},
			{
				ticket_type_idx: 4,
				expires_at: '2025-11-13T12:26:36.203Z',
				ticket_type_name: 'pzcuong',
				expiration_days: 60,
				ticket_source: 'typed',
				available_count: '7',
				expires_status: '51일 남음',
				batch_created_at: '2025-09-14T21:26:36.174Z',
			},
			{
				ticket_type_idx: 4,
				expires_at: '2025-11-14T02:03:36.900Z',
				ticket_type_name: 'pzcuong',
				expiration_days: 60,
				ticket_source: 'typed',
				available_count: '1',
				expires_status: '51일 남음',
				batch_created_at: '2025-09-15T11:03:36.899Z',
			},
			{
				ticket_type_idx: 4,
				expires_at: '2025-11-16T04:35:47.159Z',
				ticket_type_name: 'pzcuong',
				expiration_days: 60,
				ticket_source: 'typed',
				available_count: '1',
				expires_status: '53일 남음',
				batch_created_at: '2025-09-17T13:35:47.169Z',
			},
			{
				ticket_type_idx: 3,
				expires_at: null,
				ticket_type_name: null,
				expiration_days: 30,
				ticket_source: 'typed',
				available_count: '50',
				expires_status: '영구',
				batch_created_at: '2025-09-10T13:41:15.232Z',
			},
			{
				ticket_type_idx: 4,
				expires_at: null,
				ticket_type_name: 'pzcuong',
				expiration_days: 60,
				ticket_source: 'typed',
				available_count: '8',
				expires_status: '영구',
				batch_created_at: '2025-09-11T16:09:27.391Z',
			},
			{
				ticket_type_idx: 2,
				expires_at: null,
				ticket_type_name: '참여권',
				expiration_days: null,
				ticket_source: 'typed',
				available_count: '9',
				expires_status: '영구',
				batch_created_at: '2025-09-14T21:12:11.724Z',
			},
			{
				ticket_type_idx: 1,
				expires_at: null,
				ticket_type_name: '참여권',
				expiration_days: null,
				ticket_source: 'typed',
				available_count: '6',
				expires_status: '영구',
				batch_created_at: '2025-09-16T11:48:54.392Z',
			},
		],
	},
}
